import { computed, effect, inject, untracked } from '@angular/core';
import {
  patchState,
  signalStore,
  withComputed,
  withHooks,
  withMethods,
  withState,
} from '@ngrx/signals';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import {
  catchError,
  EMPTY,
  filter,
  finalize,
  type Observable,
  of,
  pipe,
  ReplaySubject,
  Subject,
  switchMap,
  takeUntil,
  tap,
  timeout,
} from 'rxjs';
import { isApiError } from '../data/api/api-error';
import { AuthApi } from '../data/api/auth.api';
import { ProfileApi, type ProfilePatch } from '../data/api/profile.api';
import type {
  AuthErrorCode,
  AuthSession,
  AuthUser,
  CodeRequest,
} from '../data/models/auth/user.model';
import { isValidUzPhone, normalizePhone } from '../shared/utils/phone';
import { readStorage, writeStorage } from '../shared/utils/storage';
import { AddressStore } from './address-store';
import { BranchStore } from './branch-store';
import { CartStore } from './cart-store';
import { NAME_MAX, SMS_RESEND_SECONDS, STORAGE_KEYS } from './constants';
import { inTelegram, telegramInitData } from './telegram-launch';
import { TokenStore } from './token-store';

export type AuthStatus = 'idle' | 'pending' | 'error';

type AuthState = {
  /** Только профиль: токенами владеет TokenStore, их читают интерцепторы на каждом запросе. */
  user: AuthUser | null;
  pendingPhone: string | null;
  /** Момент (мс), до которого сервер новую SMS не пришлёт: страница строит по нему таймер. */
  resendAvailableAtMs: number | null;
  status: AuthStatus;
  /** Код, а не текст: человеческий текст выбирает страница из словаря ngx-translate. */
  errorCode: AuthErrorCode | null;
  /** Вход через Telegram в полёте: гуард и эффект App ждут его, а не уводят гостя на /login. */
  telegramPending: boolean;
};

/** HttpClient сам не таймаутится, а пока вход через Telegram летит, гуард держит навигацию. */
const TELEGRAM_LOGIN_TIMEOUT_MS = 8_000;

/** Ограничение на ОТОБРАЖАЕМЫЕ строки — чужой мусор не должен раздувать стейт. */
const MAX_STORED_STRING = 128;

/** Ссылку на фото режем не по длине, а отбрасываем целиком: усечённый URL — это битая картинка. */
const MAX_STORED_URL = 512;

function isAuthUser(value: unknown): value is AuthUser {
  if (typeof value !== 'object' || value === null) {
    return false;
  }
  const { id, name, phone, photoUrl, bonusBalance } = value as Record<string, unknown>;
  return (
    typeof id === 'string' &&
    id !== '' &&
    (name === null || typeof name === 'string') &&
    typeof phone === 'string' &&
    phone !== '' &&
    (photoUrl === null || typeof photoUrl === 'string') &&
    typeof bonusBalance === 'number' &&
    Number.isFinite(bonusBalance)
  );
}

/** Зовётся и на свежем ответе API: профиль мог записать релиз приложения без этих лимитов. */
function sanitizeUser(user: AuthUser): AuthUser {
  const { photoUrl, bonusBalance } = user;
  return {
    id: user.id.slice(0, MAX_STORED_STRING),
    name: user.name === null ? null : user.name.slice(0, NAME_MAX),
    phone: user.phone.slice(0, MAX_STORED_STRING),
    photoUrl: photoUrl !== null && photoUrl.length <= MAX_STORED_URL ? photoUrl : null,
    bonusBalance: Number.isFinite(bonusBalance) ? Math.max(0, Math.trunc(bonusBalance)) : 0,
  };
}

/** Содержимое localStorage — недоверенный ввод: проверяются ТИПЫ полей, любая нестыковка = гость. */
function readStoredUser(): AuthUser | null {
  const raw = readStorage(STORAGE_KEYS.AUTH);
  if (!raw) {
    return null;
  }
  try {
    const parsed: unknown = JSON.parse(raw);
    return isAuthUser(parsed) ? sanitizeUser(parsed) : null;
  } catch {
    return null;
  }
}

function writeStoredUser(user: AuthUser | null): void {
  writeStorage(STORAGE_KEYS.AUTH, user === null ? null : JSON.stringify(user));
}

/** 'storage' шлёт и sessionStorage, а сессия живёт в localStorage; сам доступ к нему может бросить SecurityError. */
function isLocalStorageEvent(event: StorageEvent): boolean {
  try {
    return event.storageArea === localStorage;
  } catch {
    return false;
  }
}

/** Раз на вкладку: токены, которые сервер выдал и тут же отверг, иначе зациклили бы перезагрузки. */
function reloadOnce(): boolean {
  try {
    if (
      typeof sessionStorage === 'undefined' ||
      sessionStorage.getItem(STORAGE_KEYS.TELEGRAM_RELOAD)
    ) {
      return false;
    }
    sessionStorage.setItem(STORAGE_KEYS.TELEGRAM_RELOAD, '1');
  } catch {
    return false;
  }
  location.reload();
  return true;
}

/** Профиль без access-токена сессией не считается: refresh мог провалиться — стираем и хранилище. */
function restoreUser(tokens: TokenStore): AuthUser | null {
  if (tokens.accessToken() === null) {
    writeStoredUser(null);
    return null;
  }
  return readStoredUser();
}

/** Имя и фото берём с сервера, бонусы — нет: у `/me` для них источника нет (plan-api.md, Р-2). */
function mergeProfile(current: AuthUser | null, fresh: AuthUser): AuthUser {
  // Другой id — это другой клиент: сшивать его телефон со старым именем нельзя, берём ответ целиком.
  return current !== null && current.id === fresh.id
    ? { ...current, name: fresh.name, photoUrl: fresh.photoUrl }
    : fresh;
}

/** Локальный кулдаун поверх уже известного дедлайна: укорачивать серверную блокировку мы не вправе. */
function cooldownFrom(deadlineMs: number | null): number {
  return Math.max(deadlineMs ?? 0, Date.now() + SMS_RESEND_SECONDS * 1000);
}

/** Ошибка слоя данных → код для UI. Всё неопознанное считаем сетевым сбоем. */
function toAuthErrorCode(error: unknown): AuthErrorCode {
  return isApiError(error) && error.code !== 'unknown' ? error.code : 'network';
}

/** Живой код сервер не пересоздаёт, а отдаёт его остаток; без серверного времени — локальный фолбэк. */
function resendDeadlineMs(request: CodeRequest, nowMs: number): number {
  const ttlSec = request.ttlSec > 0 ? request.ttlSec : SMS_RESEND_SECONDS;
  const ttlMs = ttlSec * 1000;
  const requestedAtMs = Date.parse(request.requestedAtIso);
  if (!Number.isFinite(requestedAtMs)) {
    return nowMs + ttlMs;
  }
  // Часы клиента могут врать: остаток режем полным TTL, иначе кнопка залипнет на часы.
  return nowMs + Math.min(Math.max(requestedAtMs + ttlMs - nowMs, 0), ttlMs);
}

/** Root-синглтон: `/login` и `/login/confirm` живут вне shell, страничный стор потерял бы состояние. */
export const AuthStore = signalStore(
  { providedIn: 'root' },

  // Фабрика, а не литерал: профиль из storage читается при создании стора (лениво, один раз).
  withState<AuthState>(() => ({
    user: restoreUser(inject(TokenStore)),
    pendingPhone: null,
    resendAvailableAtMs: null,
    status: 'idle',
    errorCode: null,
    telegramPending: false,
  })),

  withComputed((state, tokens = inject(TokenStore)) => ({
    // Обе половины обязательны: профиль без токена даёт 401 на первом же защищённом запросе.
    isAuthed: computed((): boolean => state.user() !== null && tokens.accessToken() !== null),
    bonusBalance: computed((): number => state.user()?.bonusBalance ?? 0),
    // Признак — пустое имя, а не флаг `registered`: клиента мог завести кассир, тоже без имени.
    needsName: computed(
      (): boolean => state.user() !== null && (state.user()?.name ?? '').trim().length === 0,
    ),
  })),

  withMethods(
    (
      store,
      api = inject(AuthApi),
      profileApi = inject(ProfileApi),
      tokens = inject(TokenStore),
      addresses = inject(AddressStore),
      cart = inject(CartStore),
      branch = inject(BranchStore),
    ) => {
      const saveUser = (user: AuthUser): void => {
        const safe = sanitizeUser(user);
        patchState(store, { user: safe });
        writeStoredUser(safe);
      };

      /** Один старт сессии на SMS и Telegram: статус и ошибку здесь не трогаем — они принадлежат SMS-страницам. */
      const startSession = (session: AuthSession): void => {
        // Токены раньше профиля: isAuthed смотрит на обе половины сессии сразу.
        tokens.set({ token: session.token, refreshToken: session.refreshToken });
        saveUser(session.user);
        // На устройстве мог войти другой человек: его адреса подтянет list(), чужие показывать нельзя.
        addresses.reset();
      };

      // Выход обрывает полёты профиля: ответ /me достался бы уже мёртвой или чужой сессии.
      const aborted = new Subject<void>();

      const clearSession = (): void => {
        aborted.next();
        patchState(store, {
          user: null,
          pendingPhone: null,
          resendAvailableAtMs: null,
          status: 'idle',
          errorCode: null,
        });
        writeStoredUser(null);
        tokens.clear();
        // Адреса чужого аккаунта показывать нельзя; корзину сбрасывает только logout() — см. там.
        addresses.reset();
      };

      /** Вход другим номером в соседней вкладке: профиль, адреса и открытые экраны в памяти — прежнего человека. */
      const reloadIfAccountChanged = (): boolean => {
        const stored = readStoredUser();
        const current = store.user();
        if (stored === null || current === null || stored.id === current.id) {
          return false;
        }
        // Все сторы и экраны разом сбрасывает только перезагрузка; dropSession стёр бы из storage новую сессию.
        location.reload();
        return true;
      };

      const revokeSession = rxMethod<void>(
        pipe(
          switchMap(() =>
            // Внутри Telegram выход отвязывает его аккаунты без вопроса пользователю (гайд § 2.5).
            // Ошибку глотаем: сервер мог не ответить, но локально пользователь уже вышел.
            api.logout(inTelegram()).pipe(catchError(() => EMPTY)),
          ),
        ),
      );

      // Тихий вход через Telegram — не больше одной попытки за загрузку страницы.
      let telegramTried = false;
      // Явный выход гасит тихий вход до перезагрузки: и ответ уже летящей попытки, и новую при смерти сессии.
      let loggedOut = false;
      // ReplaySubject: конец полёта получит и гуард, подписавшийся уже после него.
      let telegramSettled: Observable<void> = of(undefined);

      /** Тихий вход через Telegram; revive — сессия умерла на открытом экране. Кроме полного ответа — молча гость. */
      const loginWithTelegram = rxMethod<boolean>(
        pipe(
          filter(
            () => !telegramTried && !loggedOut && inTelegram() && tokens.accessToken() === null,
          ),
          switchMap((revive) => {
            telegramTried = true;
            const settled = new ReplaySubject<void>(1);
            telegramSettled = settled;
            // Синхронно, до первой навигации и до эффекта App: оба видят флаг уже на этом тике.
            patchState(store, { telegramPending: true });
            return api.telegramLogin(telegramInitData()).pipe(
              timeout(TELEGRAM_LOGIN_TIMEOUT_MS),
              tap((session) => {
                // null — аккаунт не привязан: SMS спросим, когда вход понадобится. `registered` не смотрим.
                if (session === null || loggedOut) {
                  return;
                }
                startSession(session);
                // Экран, переживший смерть сессии, держит отказы своих запросов: свежим его создаст только перезагрузка.
                if (revive && reloadOnce()) {
                  return;
                }
                // Адреса сам такой экран не перезапросит, а StockStore создан не везде.
                addresses.ensureLoaded();
              }),
              // status/errorCode не трогаем: их показывают SMS-страницы, а этот вход для пользователя невидим.
              catchError(() => EMPTY),
              // Только здесь: finalize срабатывает и на ошибке, и на отписке — гуард не повиснет.
              finalize(() => {
                patchState(store, { telegramPending: false });
                settled.next();
                settled.complete();
              }),
            );
          }),
        ),
      );

      const linkTelegram = rxMethod<void>(
        pipe(
          switchMap(() =>
            // Ответа не ждём, отказ глотаем: клиент уже вошёл, привязка лишь избавит следующий запуск от SMS.
            api.linkTelegram(telegramInitData()).pipe(catchError(() => EMPTY)),
          ),
        ),
      );

      return {
        resetError(): void {
          patchState(store, { status: 'idle', errorCode: null });
        },

        logout(): void {
          loggedOut = true;
          // Запрос уходит синхронно, пока токен жив; ответа не ждём — локальный выход мгновенный.
          revokeSession();
          clearSession();
          // Только на осознанном выходе: dropSession (протухшая сессия) корзину не отнимает.
          cart.reset();
          // Точка самовывоза — тоже личная: иначе на общем телефоне чекаут покажет точку,
          // выбранную предыдущим человеком, и по ней же посчитает остатки.
          branch.reset();
        },

        /** Выход без запроса: сессия на сервере уже мертва — токены обнулил интерцептор после refresh или выход в соседней вкладке. */
        dropSession(): void {
          clearSession();
        },

        /** Для гуарда: отдаёт значение, когда полёт входа через Telegram закончился; звать, пока стоит telegramPending. */
        telegramSettled: (): Observable<void> => telegramSettled,

        _loginWithTelegram: loginWithTelegram,

        reloadIfAccountChanged,

        requestCode: rxMethod<string>(
          pipe(
            tap(() => patchState(store, { status: 'pending', errorCode: null })),
            switchMap((raw) => {
              const phone = normalizePhone(raw);
              if (!isValidUzPhone(phone)) {
                patchState(store, { status: 'error', errorCode: 'invalid_phone' });
                return EMPTY;
              }
              return api.requestCode(phone).pipe(
                tap((request) => {
                  patchState(store, {
                    status: 'idle',
                    pendingPhone: phone,
                    resendAvailableAtMs: resendDeadlineMs(request, Date.now()),
                  });
                }),
                // catchError во ВНУТРЕННЕМ pipe: подписка переживает ошибку и примет следующую попытку.
                catchError((error: unknown) => {
                  const failure = toAuthErrorCode(error);
                  // Прошлый дедлайн не сбрасываем: та SMS ушла, и её код всё ещё жив.
                  patchState(store, { status: 'error', errorCode: failure });
                  if (failure === 'too_many_requests') {
                    // Срок серверной блокировки приходит только текстом: свой кулдаун гасит долбёжку.
                    const cooldown = cooldownFrom(store.resendAvailableAtMs());
                    patchState(store, { resendAvailableAtMs: cooldown });
                  }
                  return EMPTY;
                }),
              );
            }),
          ),
        ),

        confirmCode: rxMethod<string>(
          pipe(
            tap(() => patchState(store, { status: 'pending', errorCode: null })),
            switchMap((code) => {
              const phone = store.pendingPhone();
              if (phone === null) {
                // Прямой заход на /login/confirm без запроса кода — гнать обратно на ввод номера.
                patchState(store, { status: 'error', errorCode: 'invalid_phone' });
                return EMPTY;
              }
              return api.confirmCode(phone, code).pipe(
                tap((session) => {
                  startSession(session);
                  // pendingPhone НЕ сбрасываем: страница читает его при рендере — будет гонка с навигацией.
                  patchState(store, { status: 'idle', errorCode: null });
                  // После каждого SMS-входа внутри Telegram (вызов идемпотентен): следующий запуск войдёт без SMS.
                  if (inTelegram()) {
                    linkTelegram();
                  }
                }),
                catchError((error: unknown) => {
                  patchState(store, { status: 'error', errorCode: toAuthErrorCode(error) });
                  return EMPTY;
                }),
              );
            }),
          ),
        ),

        /** Правка профиля: статус и ошибка видимы — экран ждёт ответа, прежде чем пустить дальше. */
        saveProfile: rxMethod<ProfilePatch>(
          pipe(
            tap(() => patchState(store, { status: 'pending', errorCode: null })),
            switchMap((patch) =>
              profileApi.updateProfile(patch).pipe(
                // Форму ответа PUT гайд не описывает, а адрес нового фото знает только сервер — перечитываем.
                switchMap(() => profileApi.me().pipe(catchError(() => of(null)))),
                tap((fresh) => {
                  // Ответ мог прийти в уснувшую вкладку после входа другого номера — профиль не перетираем.
                  if (reloadIfAccountChanged()) {
                    return;
                  }
                  const current = store.user();
                  if (fresh !== null) {
                    saveUser(mergeProfile(current, fresh));
                  } else if (current !== null && patch.name !== undefined) {
                    // Профиль перечитать не вышло, но PUT прошёл: имя мы знаем и без сервера.
                    saveUser({ ...current, name: patch.name });
                  }
                  patchState(store, { status: 'idle', errorCode: null });
                }),
                catchError((error: unknown) => {
                  patchState(store, { status: 'error', errorCode: toAuthErrorCode(error) });
                  return EMPTY;
                }),
                takeUntil(aborted),
              ),
            ),
          ),
        ),

        /** Тихое обновление профиля: ни статуса, ни ошибки — пользователь об этом запросе не знает. */
        refreshProfile: rxMethod<void>(
          pipe(
            filter(() => tokens.accessToken() !== null),
            switchMap(() =>
              profileApi.me().pipe(
                tap((fresh) => {
                  // Ответ мог прийти в уснувшую вкладку после входа другого номера — профиль не перетираем.
                  if (!reloadIfAccountChanged()) {
                    saveUser(mergeProfile(store.user(), fresh));
                  }
                }),
                catchError(() => EMPTY),
                takeUntil(aborted),
              ),
            ),
          ),
        ),
      };
    },
  ),

  withHooks({
    onInit(store, tokens = inject(TokenStore)) {
      // Внутри Telegram без сессии входим сразу, в фоне: стор создаётся в конструкторе App, до первой навигации.
      store._loginWithTelegram(false);

      // Токены мог обнулить интерцептор после провала refresh или выход в соседней вкладке: профиль без них — данные мёртвой сессии.
      effect(() => {
        if (tokens.accessToken() === null && untracked(store.user) !== null) {
          store.dropSession();
          // Тот же эффект, а не отдельный: флаг полёта встаёт раньше, чем эффект App уведёт гостя на /login.
          untracked(() => store._loginWithTelegram(true));
        }
      });

      // Соседняя вкладка ротирует, стирает или заводит сессию; key === null — storage очистили целиком.
      if (typeof window !== 'undefined') {
        window.addEventListener('storage', (event) => {
          const { key } = event;
          if (
            !isLocalStorageEvent(event) ||
            (key !== null && key !== STORAGE_KEYS.TOKENS && key !== STORAGE_KEYS.AUTH)
          ) {
            return;
          }
          // Вход пишет пару раньше профиля: на событии пары профиль в storage может быть ещё прежним — сверяемся на обоих ключах.
          // Другой номер — только перезагрузка: его пару не берём, чтобы до выгрузки страницы запросы не ушли с его токенами.
          if (!store.reloadIfAccountChanged()) {
            tokens.adoptStored();
          }
        });
      }
    },
  }),
);
